/* eslint-disable react/no-danger */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';

import { noop } from '@prisjakt/ui-base';
import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import CtxLink from '../render-context/ctx-link';

import acceptCookiesAction from './actions';

import { AppState } from '../../reducers';

import * as S from './styles';

type OwnProps = {};

type StateProps = {
  version: string;
};

type DispatchProps = {
  acceptCookies: (version: string) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

/**
  Cookie banner reads state from localstorage and/or cookie.
  Because of this we cannot render the cookie banner server side
  as usual since users with previous accepts will get a "flash-
  of-already-accepted-cookie-banner".

  The componentDidMount makes sure cookie banner only renders client side
* */
const CookieBanner = ({ version, acceptCookies = noop }: Props) => {
  const [isVisible, setVisibilityState] = useState(false);
  const { t, renderMode, market } = useContext(AppContext);

  useEffect(() => {
    setVisibilityState(true);
  }, []);

  const privacySettings =
    market &&
    market.privacySettings &&
    (market.privacySettings[renderMode] || market.privacySettings.responsive);

  const onAcceptCookies = useCallback(() => {
    acceptCookies(privacySettings.cookieVersion);
  }, [privacySettings, acceptCookies]);

  if (!isVisible) return null;
  if (renderMode === 'native') return null;

  if (version === privacySettings.cookieVersion) return null;

  const rootElement = document.getElementById('root-cookiebanner');

  return rootElement
    ? createPortal(
        <S.Banner id="cookie-banner" data-test="CookieBanner" isVisible>
          <S.BannerContent>
            <S.BannerText>
              <T
                domain="common"
                cookiePolicyLink={
                  <CtxLink to={privacySettings.cookieUrl} key="cookie">
                    {t('common', 'cookie-banner.cookie-policy')}
                  </CtxLink>
                }
                privacyPolicyLink={
                  <CtxLink to={privacySettings.privacyUrl} key="privacy">
                    {t('common', 'cookie-banner.privacy-policy')}
                  </CtxLink>
                }
              >
                cookie-banner.description
              </T>
              <S.PrivacyLinks>
                <CtxLink to={privacySettings.privacySettingsUrl}>
                  {t('common', 'cookie-banner.privacy-policy-settings')}
                </CtxLink>
              </S.PrivacyLinks>
            </S.BannerText>
            <S.AcceptButton
              mobile
              color="primary"
              variant="outlined"
              onClick={onAcceptCookies}
            >
              <T domain="common">button.ok</T>
            </S.AcceptButton>
            <S.AcceptButton
              desktop
              color="primary"
              variant="outlined"
              onClick={onAcceptCookies}
            >
              <T domain="common">cookie-banner.accept-button</T>
            </S.AcceptButton>
          </S.BannerContent>
        </S.Banner>,
        rootElement
      )
    : null;
};

const mapStateToProps = (state: AppState) => ({
  version: state.globals.cookieBanner.version,
});

export default connect(mapStateToProps, { acceptCookies: acceptCookiesAction })(
  CookieBanner
);
